/**
 * Gen1 Light device template
 * Used by: SHBLB-1 (Bulb), SHRGBW2 (RGBW2), SHVIN-1 (Vintage), SHBDUO-1 (Duo)
 *
 * Gen1 lights use /device/light/control endpoint
 */

import {
	createGen1TurnAction,
	createGen1StateCondition,
	createGen1StateChangedTrigger,
} from './stateManagement.js';

/**
 * Gen1 brightness action
 */
export function createGen1BrightnessAction() {
	return {
		name: 'Set Brightness',
		description: 'Adjust the brightness level',
		parameters: {
			brightness: {
				type: 'number',
				required: true,
				min: 0,
				max: 100,
				unit: 'percent',
				description: 'Brightness level (0-100)',
			},
			transition: {
				type: 'number',
				required: false,
				min: 0,
				max: 5000,
				unit: 'milliseconds',
				description: 'Transition duration',
			},
		},
		executeAction: async (device, channel, params) => {
			const { brightness, transition } = params;
			const channelId = parseInt(channel.split(':')[1]) || 0;

			const payload = {
				id: device.id,
				channel: channelId,
				brightness,
			};

			if (transition !== undefined) {
				payload.transition = transition;
			}

			return {
				endpoint: '/device/light/control',
				method: 'POST',
				params: payload,
			};
		},
	};
}

/**
 * Gen1 brightness condition
 */
export function createGen1BrightnessCondition() {
	return {
		name: 'Brightness Level',
		description: 'Check if brightness meets a condition',
		parameters: {
			operator: {
				type: 'enum',
				required: true,
				values: ['greater_than', 'less_than', 'equal_to', 'between'],
				default: 'greater_than',
				description: 'Comparison operator',
			},
			level: {
				type: 'number',
				required: true,
				min: 0,
				max: 100,
				unit: 'percent',
				description: 'Brightness level threshold',
			},
			level_max: {
				type: 'number',
				required: false,
				min: 0,
				max: 100,
				unit: 'percent',
				description: 'Upper threshold (for "between" operator)',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;
			const currentBrightness = device.status?.lights?.[channelId]?.brightness || 0;
			const { operator, level, level_max } = params;

			switch (operator) {
				case 'greater_than':
					return currentBrightness > level;
				case 'less_than':
					return currentBrightness < level;
				case 'equal_to':
					return currentBrightness === level;
				case 'between':
					return currentBrightness >= level && currentBrightness <= (level_max || 100);
				default:
					return false;
			}
		},
	};
}

export function createGen1LightTemplate(options = {}) {
	const { supportsBrightness = true, supportsColor = false } = options;

	const baseCapabilities = {
		deviceType: 'gen1_light',
		generation: 1,
		channels: ['light:0'],
		actions: {
			turn: createGen1TurnAction('light', false),
		},
		conditions: {
			isOn: createGen1StateCondition('light'),
		},
		triggers: {
			stateChanged: createGen1StateChangedTrigger('light'),
		},
	};

	if (supportsBrightness) {
		baseCapabilities.actions.setBrightness = createGen1BrightnessAction();
		baseCapabilities.conditions.brightnessLevel = createGen1BrightnessCondition();
	}

	return baseCapabilities;
}
